// Studio-side plan gating. Mirrors the backend require_feature checks: the same
// source of truth (TIER_ENTITLEMENTS in @chitos/types) is exposed by the
// me.entitlements proc, and this hook reads it to hide/lock premium UI + show
// the read-only banner. The studio bundle deliberately does NOT depend on
// @chitos/types (keeps the client lean — see PlanTier in billing.tsx), so the
// feature-key strings are mirrored here; they must match entitlements.ts values.

import { useQuery } from '@tanstack/react-query';
import { orpc } from './orpc.js';

/** Gated capabilities — string-equal to FEATURE_KEYS in @chitos/types. */
export const FEATURE = {
  OWN_NUMBER_WHATSAPP: 'own_number_whatsapp',
  OFFICIAL_WHATSAPP: 'official_whatsapp',
  EMAIL: 'email',
  VOICE: 'voice',
  BANK_RECON: 'bank_recon',
  ADVANCED_REPORTS: 'advanced_reports',
  LEDGER_PHOTO_IMPORT: 'ledger_photo_import',
  AUTOPAY: 'autopay',
  CUSTOM_DOMAIN: 'custom_domain',
  BRANDING: 'branding',
  TAX_EXPORT: 'tax_export',
  AUDIT_LOG: 'audit_log',
  KYC_OCR: 'kyc_ocr',
  AI_RISK: 'ai_risk',
  API_ACCESS: 'api_access',
  PRIORITY_SUPPORT: 'priority_support',
  MULTI_LOCALE: 'multi_locale',
} as const;

export type FeatureKey = (typeof FEATURE)[keyof typeof FEATURE];

export interface Entitlements {
  plan_tier: string;
  status: string;
  features: string[];
  limits: { max_chits: number | null; max_members: number | null; max_staff: number | null };
}

/** The lowest tier that unlocks each feature — drives the "Upgrade to X" copy.
 *  Matches the layering in TIER_ENTITLEMENTS (Growth adds most, Scale adds the
 *  per-scan/AI ones). */
const SCALE_ONLY: ReadonlySet<string> = new Set([
  FEATURE.KYC_OCR,
  FEATURE.AI_RISK,
  FEATURE.API_ACCESS,
  FEATURE.PRIORITY_SUPPORT,
  FEATURE.MULTI_LOCALE,
]);

export function min_tier_for(key: string): 'growth' | 'scale' {
  return SCALE_ONLY.has(key) ? 'scale' : 'growth';
}

export interface UseEntitlements {
  entitlements: Entitlements | undefined;
  is_loading: boolean;
  has_feature: (key: string) => boolean;
  is_read_only: boolean;
}

/** Fetch the current tenant's plan entitlements (cached 5 min app-wide). */
export function use_entitlements(): UseEntitlements {
  const q = useQuery({
    queryKey: ['me-entitlements'],
    queryFn: () => orpc.me.entitlements({}),
    staleTime: 5 * 60_000,
  });
  const ent = q.data?.entitlements as Entitlements | undefined;
  return {
    entitlements: ent,
    is_loading: q.isLoading,
    // Optimistic-allow while loading so the UI doesn't flash locks. The backend
    // still enforces every call, so an over-permissive UI can't grant access.
    has_feature: (key: string): boolean => !ent || ent.features.includes(key),
    is_read_only: ent ? ent.status === 'past_due' || ent.status === 'suspended' : false,
  };
}
